import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useEnrollmentDocuments, useUploadEnrollmentDocument } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../constants/enrollmentContent'
import { formatStoredDate, MAX_LICENSE_FILE_SIZE } from './helpers'

type UseLicenseDocumentUploadArgs = {
  driverLicenseNumber: string
  driverLicenseState: string
  licenseExpirationDate: string
}

export function useLicenseDocumentUpload({
  driverLicenseNumber,
  driverLicenseState,
  licenseExpirationDate
}: UseLicenseDocumentUploadArgs) {
  const enrollmentSessionToken = useEnrollmentStore(state => state.sessionToken)
  const updateUploadedDocumentFiles = useEnrollmentStore(state => state.updateUploadedDocumentFiles)
  const uploadEnrollmentDocumentMutation = useUploadEnrollmentDocument()
  const documentsQuery = useEnrollmentDocuments(enrollmentSessionToken)
  const licenseUploadInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadedLicenseFileName, setUploadedLicenseFileName] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)

  useEffect(() => {
    const driverLicenseDocument = (documentsQuery.data ?? [])
      .filter(
        document => document.documentType === 'DRIVERS_LICENSE' && document.status !== 'REJECTED'
      )
      .sort((left, right) => (right.uploadedAt ?? '').localeCompare(left.uploadedAt ?? ''))[0]

    const fileName = driverLicenseDocument?.fileName ?? ''
    setUploadedLicenseFileName(fileName)
    updateUploadedDocumentFiles({ driverLicense: fileName })
  }, [documentsQuery.data, updateUploadedDocumentFiles])

  const handleDriverLicenseFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    setUploadError(null)

    if (!enrollmentSessionToken) {
      setUploadError(ENROLLMENT_COMMON_MESSAGES.sessionExpired)
      event.target.value = ''
      return
    }

    if (file.size > MAX_LICENSE_FILE_SIZE) {
      setUploadError('Driver license file must be 10MB or smaller.')
      event.target.value = ''
      return
    }

    try {
      const uploadedDocument = await uploadEnrollmentDocumentMutation.mutateAsync({
        sessionToken: enrollmentSessionToken,
        documentType: 'DRIVERS_LICENSE',
        file,
        documentNumber: driverLicenseNumber.trim() || undefined,
        issuingState: driverLicenseState.trim() || undefined,
        expiryDate: formatStoredDate(licenseExpirationDate) || undefined
      })

      setUploadedLicenseFileName(uploadedDocument.fileName || file.name)
      updateUploadedDocumentFiles({ driverLicense: uploadedDocument.fileName || file.name })
      void documentsQuery.refetch()
    } catch (error) {
      setUploadError(
        error instanceof Error
          ? error.message
          : 'Unable to upload your driver license right now. Please try again.'
      )
    } finally {
      event.target.value = ''
    }
  }

  return {
    licenseUploadInputRef,
    uploadedLicenseFileName,
    uploadError,
    isUploading: uploadEnrollmentDocumentMutation.isPending,
    setUploadError,
    handleDriverLicenseFileChange
  }
}
